import type { TrainingModeCode } from '@golf/common';

/* Icônes des modes d'entraînement.
 *
 * Dessinées plutôt que prises dans lucide : « pieds joints » n'a pas
 * d'équivalent dans un jeu d'icônes générique, et un pictogramme approximatif
 * dirait mal ce que l'exercice demande.
 */

interface Props {
    mode: TrainingModeCode;
    size?: number;
    className?: string;
}

/** Trois balles alignées : le rythme du Triple Shot, vu de dessus. */
function TripleShot({ size, className }: { size: number; className: string }) {
    return (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
            aria-hidden="true" className={className}>
            <circle cx="5" cy="12" r="2.6" fill="currentColor" />
            <circle cx="12" cy="12" r="2.6" fill="currentColor" />
            <circle cx="19" cy="12" r="2.6" fill="currentColor" />
            <path d="M4 18.5 h16" stroke="currentColor" strokeWidth="1.6"
                strokeLinecap="round" opacity="0.35" />
        </svg>
    );
}

/** Les deux appuis vus de dessus, jointifs, et la balle devant.
 *
 *  Première tentative : une silhouette de face, jambes serrées. Rendue à 30 px
 *  — la taille réelle — elle se lisait « I » : jambes et pieds fusionnaient en
 *  un seul bloc vertical. Six variantes ont été comparées côte à côte à 110 et
 *  à 30 px ; seule celle-ci survit à la réduction.
 *
 *  Vue de dessus, l'exercice tient en une image : deux appuis qui se touchent,
 *  et la balle en face. C'est aussi le pictogramme que les joueurs
 *  reconnaissent. */
function FeetTogether({ size, className }: { size: number; className: string }) {
    return (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
            aria-hidden="true" className={className}>
            {/* Les deux pieds, séparés d'un seul point : c'est l'écart nul qui
                nomme l'exercice, et il doit rester visible à petite taille. */}
            <rect x="6.6" y="5" width="4.8" height="14" rx="2.4" fill="currentColor" />
            <rect x="12.6" y="5" width="4.8" height="14" rx="2.4" fill="currentColor" />
            {/* La balle, devant les appuis. Discrète : elle situe la scène sans
                concurrencer les pieds. */}
            <circle cx="12" cy="2.6" r="1.7" fill="currentColor" opacity="0.6" />
        </svg>
    );
}

/** Une fiche écrite : c'est la phrase du joueur qui définit l'exercice.
 *
 *  Rien d'autre ne peut le représenter — le mode n'impose ni rythme, ni nombre
 *  de balles, ni posture. Dessiner un club ou une balle le confondrait avec les
 *  deux autres ; la fiche dit exactement ce qui le distingue.
 *
 *  Trois traits pleins de longueurs inégales : à 22 px, deux se lisent comme un
 *  signe « égal » et quatre se referment en aplat gris. */
function FreeSession({ size, className }: { size: number; className: string }) {
    return (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
            aria-hidden="true" className={className}>
            <rect x="3.5" y="3" width="17" height="18" rx="2.6"
                stroke="currentColor" strokeWidth="1.8" />
            <path d="M7.5 9h9M7.5 12.5h9M7.5 16h5.5" stroke="currentColor"
                strokeWidth="1.8" strokeLinecap="round" />
        </svg>
    );
}

export function ModeIcon({ mode, size = 28, className = '' }: Props) {
    if (mode === 'feet_together') return <FeetTogether size={size} className={className} />;
    if (mode === 'free') return <FreeSession size={size} className={className} />;
    return <TripleShot size={size} className={className} />;
}
